/**
 * Measure paint — what colour each point and line on the face should be.
 * ----------------------------------------------------------------------
 *
 * A drawing in one colour says "these points were used". A drawing in the
 * right colours says what the measurement FOUND. So every point a measure
 * names gets a colour, and the colour is never decoration:
 *
 *   graded    the agent scored each area of the face (Tern's per-region
 *             symmetry), so each point takes its own area's band on the SAME
 *             scale the written report uses — 90 and above, 75 and above,
 *             below that. This is the colour map the report shows, put back
 *             on the face it was measured from.
 *   sequence  the measure's lines are separate quantities in order — the three
 *             facial thirds — so each segment gets its own hue and its own name.
 *   banded    the measure itself carries one 0–100 score (`summaryKey`), so the
 *             whole drawing takes that score's band.
 *   grouped   no scores, but the areas are known, so points are coloured by
 *             area: at a glance, which parts of the face this reading looked at.
 *   plain     nothing known about the points beyond their coordinates. One
 *             colour, honestly.
 *
 * Mode is chosen in that order, and falls back on its own: a bot that stops
 * emitting region scores degrades to `grouped`, then to `plain`, and the page
 * keeps working.
 *
 * WHERE THE BOT-SPECIFIC KNOWLEDGE LIVES: not here. This module is generic. The
 * map from a landmark index to the area the bot groups it under, which summary
 * fields are 0–100 scores, and which measures are ordered sequences all come
 * from the bot's own descriptor in `reportAdapters.js` — the single file that
 * knows about bots. Swapping a bot means editing that descriptor; this file and
 * every component stay as they are.
 *
 * Colour is never the only carrier: every group this returns is named in the
 * legend, with its score where there is one, so the picture reads the same to
 * someone who cannot tell the bands apart.
 */

/**
 * The report's own scale, and nothing invented beside it.
 *
 * `report` is the exact colour the generated report prints for that band.
 * `draw` is the same hue lifted for legibility ON a photograph — the report
 * puts these on pale chips, where a deep green reads fine; over skin and hair
 * it does not. The band boundaries are the report's: >= 90, >= 75, below.
 */
export const BANDS = [
  { key: 'high', min: 90, report: '#2E7D55', draw: '#35A06B' },
  { key: 'mid', min: 75, report: '#8A5A1E', draw: '#D79A2B' },
  { key: 'low', min: -Infinity, report: '#9A362F', draw: '#DE5A4E' },
];

/** The band a 0–100 score falls in. */
export function bandFor(score) {
  return BANDS.find((b) => score >= b.min) || BANDS[BANDS.length - 1];
}

/**
 * Hues for telling things apart rather than ranking them (areas, segments).
 *
 * Distinct in hue, close in lightness, so no one group looks more important
 * than another, and all of them hold up over skin with a dark underlay.
 */
export const CATEGORICAL = [
  '#5B9BD6', '#E0A84B', '#A98CD9', '#49C0A8', '#E97FA8', '#D7B98A', '#7FD1E8',
];

/** The brand's candlelight: the measurement, when there is nothing to encode. */
export const PLAIN = '#C0A786';

/** Index into a palette without ever running off the end. */
const pick = (palette, i) => palette[((i % palette.length) + palette.length) % palette.length];

/** A number, or null — the bots legitimately emit null for an unavailable figure. */
function score(v) {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/**
 * Which area each of a measure's points belongs to, as the bot groups them.
 * Points the bot's map says nothing about come back as null, which every mode
 * below treats as "draw it, say nothing about it".
 */
function regionsOf(measure, paint) {
  const map = paint?.region || {};
  const out = new Map();
  (measure?.points || []).forEach((i) => out.set(i, map[i] ?? null));
  // A line's endpoints can sit in different areas (a line from brow to eyelid);
  // such a line belongs to neither, and is drawn plain.
  return out;
}

/** The areas a measure touches, in the bot's declared order so a colour means
 *  the same thing on every step of the report. */
function orderedRegions(present, paint) {
  const declared = paint?.regionOrder || [];
  const known = declared.filter((r) => present.has(r));
  const extra = [...present].filter((r) => r && !declared.includes(r)).sort();
  return [...known, ...extra];
}

/**
 * Step back everything outside the areas this measure is actually about.
 *
 * Some measures name far more points than they are read around: Tern records
 * every paired landmark on all eleven muscle measures, so without this every
 * muscle step would draw the same thirty points across the whole face and the
 * eleven steps would look identical. The bot's own group definition says which
 * area each one concerns, mirrored in the bot's descriptor as `focus`.
 *
 * Nothing is removed — the points the agent recorded are all still drawn, just
 * as faint context behind the ones the measurement is about. If the mirrored
 * table ever drifts from the bot, the cost is a wrong emphasis, never a wrong
 * or invented point.
 */
function applyFocus(result, focusRegions) {
  if (!Array.isArray(focusRegions) || !focusRegions.length) return result;
  const inFocus = (g) => g != null && focusRegions.includes(g);
  const anyFocused = result.points.some((p) => inFocus(p.group));
  if (!anyFocused) return result;        // the bot moved on; emphasise nothing
  const muted = result.points.some((p) => !inFocus(p.group));
  return {
    ...result,
    focused: true,
    points: result.points.map((p) => ({ ...p, muted: !inFocus(p.group) })),
    lines: result.lines.map((l) => ({ ...l, muted: !inFocus(l.group) })),
    legend: [
      ...result.legend.filter((row) => inFocus(row.group)),
      ...(muted ? [{
        group: 'context', color: PLAIN, kind: 'context',
        labelKey: 'candleCard.paint.context', label: 'context',
        score: null, band: null, muted: true,
      }] : []),
    ],
  };
}

/**
 * Decide the colouring for one measure.
 *
 * @param {object} measure  the agent's measure map entry: {key, points, lines, summaryKey}
 * @param {object} metrics  the agent's summary, minus the measure map
 * @param {object} paint    the bot's paint descriptor from reportAdapters
 * @returns {{
 *   mode: 'graded'|'sequence'|'banded'|'grouped'|'plain',
 *   points: Array<{index: number, color: string, group: string|null}>,
 *   lines: Array<{from: number, to: number, color: string, group: string|null}>,
 *   legend: Array<{group: string, color: string, kind: string, labelKey: string,
 *                  label: string, score: number|null, band: string|null}>,
 * }}
 */
function decide(measure, metrics, paint) {
  const pts = measure?.points || [];
  const lines = measure?.lines || [];
  const plainPoints = () => pts.map((i) => ({ index: i, color: PLAIN, group: null }));
  const plainLines = () => lines
    .filter((l) => Array.isArray(l) && l.length >= 2)
    .map(([a, b]) => ({ from: a, to: b, color: PLAIN, group: null }));

  // ---- sequence: the lines are separate, ordered quantities ---------------
  // Checked before the scores because a measure built as a sequence (the facial
  // thirds) is three readings, not one: colouring all three the same band would
  // hide exactly what it is there to show.
  const segments = paint?.segments?.[measure?.key];
  if (Array.isArray(segments) && segments.length && lines.length) {
    const drawn = lines.filter((l) => Array.isArray(l) && l.length >= 2);
    return {
      mode: 'sequence',
      // A point can sit on two segments (the thirds share their boundaries), so
      // it takes the colour of the first segment that uses it; the segments
      // themselves carry the meaning through their lines.
      points: pts.map((i) => {
        const at = drawn.findIndex(([a, b]) => a === i || b === i);
        return {
          index: i,
          color: at < 0 ? PLAIN : pick(CATEGORICAL, at),
          group: at < 0 ? null : (segments[at] ?? null),
        };
      }),
      lines: drawn.map(([a, b], i) => ({
        from: a, to: b, color: pick(CATEGORICAL, i), group: segments[i] ?? null,
      })),
      legend: drawn.slice(0, segments.length).map((_, i) => ({
        group: segments[i],
        color: pick(CATEGORICAL, i),
        kind: 'segment',
        labelKey: `candleCard.segment.${segments[i]}`,
        label: segments[i],
        score: null,
        band: null,
      })),
    };
  }

  const byPoint = regionsOf(measure, paint);
  const present = new Set([...byPoint.values()].filter(Boolean));
  const regionScores = paint?.regionScoreKey ? metrics?.[paint.regionScoreKey] : null;
  const graded = present.size > 0 && regionScores && typeof regionScores === 'object'
    && [...present].some((r) => score(regionScores[r]) !== null);

  // ---- graded: each area carries its own score, on the report's scale -----
  if (graded) {
    const colorOf = (region) => {
      const v = score(regionScores[region]);
      return v === null ? PLAIN : bandFor(v).draw;
    };
    const regionOfLine = ([a, b]) => {
      const ra = byPoint.get(a) ?? null;
      return ra && ra === (byPoint.get(b) ?? null) ? ra : null;
    };
    const scored = orderedRegions(present, paint)
      .filter((r) => score(regionScores[r]) !== null);
    const unscored = pts.some((i) => {
      const r = byPoint.get(i);
      return !r || score(regionScores[r]) === null;
    });
    return {
      mode: 'graded',
      points: pts.map((i) => {
        const r = byPoint.get(i) ?? null;
        return { index: i, color: r ? colorOf(r) : PLAIN, group: r };
      }),
      lines: lines.filter((l) => Array.isArray(l) && l.length >= 2).map(([a, b]) => {
        const r = regionOfLine([a, b]);
        return { from: a, to: b, color: r ? colorOf(r) : PLAIN, group: r };
      }),
      legend: [
        ...scored.map((r) => {
          const v = score(regionScores[r]);
          return {
            group: r,
            color: bandFor(v).draw,
            kind: 'area',
            labelKey: `candleCard.area.${r}`,
            label: r,
            score: v,
            band: bandFor(v).key,
          };
        }),
        ...(unscored ? [{
          group: 'other', color: PLAIN, kind: 'other',
          labelKey: 'candleCard.paint.other', label: 'other', score: null, band: null,
        }] : []),
      ],
    };
  }

  // ---- banded: the measure's own score grades the whole drawing -----------
  const key = measure?.summaryKey;
  const ownScore = key && (paint?.scoreKeys || []).includes(key)
    ? score(metrics?.[key]) : null;
  if (ownScore !== null) {
    const band = bandFor(ownScore);
    return {
      mode: 'banded',
      points: pts.map((i) => ({ index: i, color: band.draw, group: band.key })),
      lines: plainLines().map((l) => ({ ...l, color: band.draw, group: band.key })),
      legend: [{
        group: band.key,
        color: band.draw,
        kind: 'band',
        labelKey: `candleCard.paint.band.${band.key}`,
        label: band.key,
        score: ownScore,
        band: band.key,
      }],
    };
  }

  // ---- grouped: no scores, but we know which part of the face is which ----
  if (present.size > 1) {
    const order = orderedRegions(present, paint);
    const colorOf = (r) => (r ? pick(CATEGORICAL, order.indexOf(r)) : PLAIN);
    return {
      mode: 'grouped',
      points: pts.map((i) => {
        const r = byPoint.get(i) ?? null;
        return { index: i, color: colorOf(r), group: r };
      }),
      lines: lines.filter((l) => Array.isArray(l) && l.length >= 2).map(([a, b]) => {
        const ra = byPoint.get(a) ?? null;
        const r = ra && ra === (byPoint.get(b) ?? null) ? ra : null;
        return { from: a, to: b, color: colorOf(r), group: r };
      }),
      legend: order.map((r) => ({
        group: r,
        color: colorOf(r),
        kind: 'area',
        labelKey: `candleCard.area.${r}`,
        label: r,
        score: null,
        band: null,
      })),
    };
  }

  // ---- plain ---------------------------------------------------------------
  return {
    mode: 'plain',
    points: plainPoints(),
    lines: plainLines(),
    legend: [{
      group: [...present][0] || 'points',
      color: PLAIN,
      kind: present.size === 1 ? 'area' : 'plain',
      labelKey: present.size === 1
        ? `candleCard.area.${[...present][0]}` : 'candleCard.paint.points',
      label: [...present][0] || 'points',
      score: null,
      band: null,
    }],
  };
}

/**
 * The colouring for one measure: the mode, a colour per point and per line, and
 * the legend that names every colour used.
 *
 * @param {object} measure  the agent's measure map entry
 * @param {object} [metrics] the agent's summary, minus the measure map
 * @param {object} [paint]  the bot's paint descriptor from reportAdapters
 */
export function paintMeasure(measure, metrics = {}, paint = null) {
  const result = decide(measure, metrics || {}, paint || null);
  return applyFocus(result, paint?.focus?.[measure?.key]);
}

export default paintMeasure;
